import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LIST_VISIBILITY_LABEL, formatPrice, type ItemDto } from '@wishare/shared';
import { api, ApiError } from '../lib/api.js';
import { formatRelativeDays, minorToPriceInput, pluralize, priceInputToMinor } from '../lib/format.js';
import { availableFilters, FILTER_LABEL, filterItems, type ItemFilter } from '../lib/filter.js';
import { ItemCard } from '../components/ItemCard.js';
import { ItemForm, type ItemFormValues } from '../components/ItemForm.js';
import { Avatar, Badge, Button, Confirm, EmptyState, Modal, Notice, SelectField, TextAreaField, TextField } from '../components/ui.js';
import { SkeletonHeader, SkeletonItems } from '../components/Skeleton.js';
import { IconCheck, IconEyeOff, IconLink, IconLock, IconSearch } from '../components/icons.js';

/**
 * Страница списка.
 *
 * Один и тот же экран обслуживает два очень разных сценария:
 *  - владелец наполняет список и никогда не видит броней;
 *  - друг заходит выбрать подарок и видит, что уже разобрали.
 *
 * Разделение делает сервер: во втором случае у желаний заполнено поле
 * reservation, в первом оно пустое. Страница просто отражает это.
 */

export function ListPage(): JSX.Element {
  const { listId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [deleting, setDeleting] = useState<ItemDto | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** Желание, к сбору на которое сейчас присоединяются (спрашиваем сумму). */
  const [contributing, setContributing] = useState<ItemDto | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ItemFilter>('all');

  const queryKey = ['list', listId];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.lists.one(listId),
    enabled: Boolean(listId),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ['lists', 'mine'] });
  };

  const createItem = useMutation({
    mutationFn: (values: ItemFormValues) => api.items.create(listId, toPayload(values)),
    onSuccess: () => {
      refresh();
      setAdding(false);
    },
  });

  const updateItem = useMutation({
    mutationFn: ({ itemId, values }: { itemId: string; values: ItemFormValues }) =>
      api.items.update(itemId, toPayload(values)),
    onSuccess: () => {
      refresh();
      setEditing(null);
    },
  });

  const deleteItem = useMutation({
    mutationFn: (itemId: string) => api.items.remove(itemId),
    onSuccess: () => {
      refresh();
      setDeleting(null);
    },
  });

  const reserve = useMutation({
    mutationFn: ({ itemId, contributionMinor }: { itemId: string; contributionMinor?: number | null }) =>
      api.reservations.create(itemId, {
        quantity: 1,
        ...(contributionMinor !== undefined ? { contributionMinor } : {}),
      }),
    onSettled: () => {
      setBusyItemId(null);
      setContributing(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['gifts'] });
    },
  });

  const cancelReserve = useMutation({
    mutationFn: (itemId: string) => api.reservations.remove(itemId),
    onSettled: () => {
      setBusyItemId(null);
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['gifts'] });
    },
  });

  /**
   * Перестановка желаний.
   *
   * Порядок меняем сразу в уже загруженных данных, не дожидаясь ответа сервера:
   * иначе карточка после нажатия стрелки на мгновение остаётся на месте и
   * «прыгает» позже — выглядит как сбой. Если сервер откажет, следующее
   * обновление вернёт настоящий порядок.
   */
  const reorder = useMutation({
    mutationFn: (itemIds: string[]) => api.lists.reorder(listId, itemIds),
    onMutate: (itemIds) => {
      queryClient.setQueryData(queryKey, (current: typeof data | undefined) => {
        if (!current) return current;
        const byId = new Map(current.items.map((entry) => [entry.id, entry]));
        const reordered = itemIds
          .map((id) => byId.get(id))
          .filter((entry): entry is ItemDto => Boolean(entry));
        return { ...current, items: reordered };
      });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });

  const removeList = useMutation({
    mutationFn: () => api.lists.remove(listId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lists', 'mine'] });
      navigate('/');
    },
  });

  /*
   * Шапка списка приезжает вместе с данными, поэтому вместо неё — заготовка
   * шапки: без неё сетка желаний при загрузке стоит вплотную к меню, а когда
   * приедет название, уезжает вниз на его высоту, и человек теряет то место,
   * куда смотрел.
   */
  if (isLoading) {
    return (
      <>
        <SkeletonHeader />
        <SkeletonItems />
      </>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<IconLock />}
        title="Список недоступен"
        text={
          error instanceof ApiError && error.status === 404
            ? 'Возможно, его удалили или он открыт только друзьям владельца.'
            : 'Не удалось загрузить список.'
        }
        action={
          <Button variant="ghost" onClick={() => navigate('/')}>
            На главную
          </Button>
        }
      />
    );
  }

  const { list, items } = data;

  // Отбор идёт в браузере: желания уже загружены, и гонять запрос на сервер
  // ради подстроки — только добавлять задержку там, где ответ мгновенный.
  const filters = availableFilters(items, list.viewerIsOwner);
  const visibleItems = filterItems(items, query, filter);

  /*
   * При активном отборе перестановка прячется. Двигать желание относительно
   * соседей, которых сейчас не видно, — значит получить непредсказуемый
   * результат: человек нажимает «вверх», а карточка прыгает через три
   * скрытых позиции.
   */
  const isFiltered = query.trim().length > 0 || filter !== 'all';

  const shareUrl = list.shareToken ? `${window.location.origin}/s/${list.shareToken}` : null;

  async function copyShareLink(): Promise<void> {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Буфер обмена может быть недоступен (старый браузер, отсутствие https) —
      // тогда просто показываем ссылку, чтобы её выделили руками.
      window.prompt('Скопируйте ссылку:', shareUrl);
    }
  }

  return (
    <>
      <div className="page-header">
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 'var(--space-3)' }}>
            <span style={{ fontSize: '2rem' }} aria-hidden="true">
              {list.coverEmoji || '🎁'}
            </span>
            <div className="grow" style={{ minWidth: 0 }}>
              <h1 className="truncate">{list.title}</h1>
              <div className="row row--wrap tiny muted" style={{ gap: 'var(--space-2)' }}>
                {!list.viewerIsOwner && (
                  <span className="row" style={{ gap: 6 }}>
                    <Avatar name={list.owner.displayName} src={list.owner.avatarUrl} size="sm" />
                    {list.owner.displayName}
                  </span>
                )}
                <span>{pluralize(list.itemCount, 'желание', 'желания', 'желаний')}</span>
                {list.viewerIsOwner && <span>· {LIST_VISIBILITY_LABEL[list.visibility]}</span>}
                {list.eventDate && <span>· {formatRelativeDays(list.eventDate)}</span>}
              </div>
            </div>
          </div>
          {list.description && <p className="muted small" style={{ marginTop: 'var(--space-3)' }}>{list.description}</p>}
        </div>

        <div className="row" style={{ gap: 'var(--space-2)' }}>
          {list.viewerIsOwner && (
            <>
              <Button variant="ghost" onClick={() => setShowSettings(true)}>
                Настройки
              </Button>
              <Button variant="primary" onClick={() => setAdding(true)}>
                + Желание
              </Button>
            </>
          )}
        </div>
      </div>

      {/*
        Обещание продукта, сказанное вслух. Владелец должен понимать, почему он
        не видит броней, — иначе это выглядит как недоделка, а не как замысел.
      */}
      {list.viewerIsOwner && list.itemCount > 0 && (
        <Notice tone="info" icon={<IconEyeOff size={19} />}>
          <span className="small">
            Кто-то, возможно, уже выбрал вам подарок из этого списка — но мы вам не скажем. Так задумано.
          </span>
        </Notice>
      )}

      {shareUrl && (
        <div className="row row--wrap" style={{ margin: 'var(--space-4) 0' }}>
          {/*
            Значки рисованные, а не эмодзи: 🔗 и ✓ система рисует по-своему на
            каждом устройстве, а кнопка эта стоит рядом с рисованными значками
            карточек — разнобой виден сразу.
          */}
          <Button variant="ghost" size="sm" onClick={() => void copyShareLink()}>
            {copied ? <IconCheck size={17} /> : <IconLink size={17} />}
            {copied ? 'Ссылка скопирована' : 'Скопировать ссылку на список'}
          </Button>
          <span className="tiny faint">Ею можно поделиться с теми, кого нет в друзьях</span>
        </div>
      )}

      {/*
        Поиск появляется, только когда список дорос до размера, в котором
        глазами уже не находишь. На пяти желаниях строка поиска — лишний
        элемент, который нужно взглядом перепрыгнуть.
      */}
      {items.length >= 8 && (
        <div className="stack stack--sm" style={{ margin: 'var(--space-4) 0' }}>
          <TextField
            placeholder="Найти среди желаний"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Поиск по списку"
          />
          {filters.length > 0 && (
            <div className="chip-row">
              {filters.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`chip ${filter === value ? 'chip--active' : ''}`.trim()}
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                >
                  {FILTER_LABEL[value]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={<IconLink />}
          title={list.viewerIsOwner ? 'Добавьте первое желание' : 'Список пока пуст'}
          text={
            list.viewerIsOwner
              ? 'Скопируйте ссылку на товар откуда угодно и вставьте сюда — фотографию и цену подставим сами.'
              : 'Владелец ещё ничего не добавил. Загляните позже.'
          }
          action={
            list.viewerIsOwner ? (
              <Button variant="primary" size="lg" onClick={() => setAdding(true)}>
                Вставить ссылку
              </Button>
            ) : undefined
          }
        />
      ) : visibleItems.length === 0 ? (
        <EmptyState
          icon={<IconSearch />}
          title="Ничего не нашлось"
          text="Попробуйте другое слово или снимите отбор."
          action={
            <Button
              variant="ghost"
              onClick={() => {
                setQuery('');
                setFilter('all');
              }}
            >
              Показать все
            </Button>
          }
        />
      ) : (
        <div className="items-grid" style={{ marginTop: 'var(--space-5)' }}>
          {visibleItems.map((item, index) => (
            <ItemCard
              key={item.id}
              item={item}
              isOwner={list.viewerIsOwner}
              canReserve={list.viewerCanReserve}
              busy={busyItemId === item.id}
              canMoveUp={!isFiltered && index > 0}
              canMoveDown={!isFiltered && index < visibleItems.length - 1}
              onMove={(target, direction) => {
                const order = items.map((entry) => entry.id);
                const from = order.indexOf(target.id);
                const to = from + direction;
                if (to < 0 || to >= order.length) return;
                // Меняем соседей местами и отправляем весь новый порядок.
                [order[from], order[to]] = [order[to]!, order[from]!];
                reorder.mutate(order);
              }}
              onEdit={setEditing}
              onDelete={setDeleting}
              onReserve={(target) => {
                // На складчину сначала спрашиваем сумму, обычное желание
                // бронируется сразу — лишний вопрос там ни к чему.
                if (target.groupGift) {
                  setContributing(target);
                  return;
                }
                setBusyItemId(target.id);
                reserve.mutate({ itemId: target.id });
              }}
              onCancelReservation={(target) => {
                setBusyItemId(target.id);
                cancelReserve.mutate(target.id);
              }}
            />
          ))}
        </div>
      )}

      {(reserve.error || cancelReserve.error) && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Notice tone="danger">
            {(reserve.error ?? cancelReserve.error) instanceof ApiError
              ? (reserve.error ?? cancelReserve.error as ApiError).message
              : 'Не удалось изменить бронь'}
          </Notice>
        </div>
      )}

      {/* --- Окна --- */}

      <Modal open={adding} title="Новое желание" onClose={() => setAdding(false)}>
        <ItemForm onSubmit={(values) => createItem.mutateAsync(values).then(() => undefined)} onCancel={() => setAdding(false)} />
      </Modal>

      <Modal open={Boolean(editing)} title="Изменить желание" onClose={() => setEditing(null)}>
        {editing && (
          <div className="stack">
            <MoveToListField item={editing} currentListId={listId} onMoved={() => { refresh(); setEditing(null); }} />
            <hr className="divider" />
            <ItemForm
              initial={editing}
              submitLabel="Сохранить"
              onSubmit={(values) => updateItem.mutateAsync({ itemId: editing.id, values }).then(() => undefined)}
              onCancel={() => setEditing(null)}
            />
          </div>
        )}
      </Modal>

      <Confirm
        open={Boolean(deleting)}
        title="Удалить желание?"
        message={`«${deleting?.title ?? ''}» исчезнет из списка. Это действие нельзя отменить.`}
        loading={deleteItem.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteItem.mutate(deleting.id)}
      />

      <ContributionModal
        item={contributing}
        onClose={() => setContributing(null)}
        onConfirm={(contributionMinor) => {
          if (!contributing) return;
          setBusyItemId(contributing.id);
          reserve.mutate({ itemId: contributing.id, contributionMinor });
        }}
        pending={reserve.isPending}
      />

      <ListSettingsModal
        open={showSettings}
        list={list}
        onClose={() => setShowSettings(false)}
        onSaved={refresh}
        onDelete={() => removeList.mutate()}
        deleting={removeList.isPending}
      />
    </>
  );
}

function toPayload(values: ItemFormValues): Record<string, unknown> {
  return {
    title: values.title,
    description: values.description || undefined,
    url: values.url,
    imageUrl: values.imageUrl,
    imageCandidates: values.imageCandidates,
    priceMinor: values.priceMinor,
    currency: values.currency,
    priority: values.priority,
    quantity: values.quantity,
    groupGift: values.groupGift,
    sourceSite: values.sourceSite,
  };
}

/**
 * Перенос желания в другой список.
 *
 * Живой сценарий: «Хочу когда-нибудь» разрастается, и часть желаний переезжает
 * в конкретный «День рождения». Без этого приходится удалять и заводить заново,
 * теряя и фотографию, и цену, и — что хуже — чужую бронь.
 *
 * Показывается только если списков больше одного: переносить некуда — незачем
 * и спрашивать.
 */
function MoveToListField({
  item,
  currentListId,
  onMoved,
}: {
  item: ItemDto;
  currentListId: string;
  onMoved: () => void;
}): JSX.Element | null {
  const { data: lists } = useQuery({ queryKey: ['lists', 'mine'], queryFn: api.lists.mine });
  const [busy, setBusy] = useState(false);

  const targets = (lists ?? []).filter((list) => list.id !== currentListId && !list.isArchived);
  if (targets.length === 0) return null;

  return (
    <SelectField
      label="Перенести в другой список"
      value=""
      onChange={(nextListId) => {
        if (!nextListId || busy) return;
        setBusy(true);
        api.items
          .update(item.id, { listId: nextListId })
          .then(onMoved)
          .finally(() => setBusy(false));
      }}
      options={[
        { value: '', label: busy ? 'Переносим…' : 'Оставить здесь' },
        ...targets.map((list) => ({
          value: list.id,
          label: `${list.coverEmoji || '🎁'} ${list.title}`,
        })),
      ]}
    />
  );
}

/**
 * Сколько человек вносит в складчину.
 *
 * Сумма необязательна: кто-то готов сказать «участвую, доложу сколько нужно».
 * Заставлять называть цифру в такой момент — значит терять участников,
 * поэтому поле можно оставить пустым.
 */
interface ContributionModalProps {
  item: ItemDto | null;
  onClose: () => void;
  onConfirm: (contributionMinor: number | null) => void;
  pending: boolean;
}

function ContributionModal({ item, onClose, onConfirm, pending }: ContributionModalProps): JSX.Element | null {
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (!item) return;
    // Подсказываем разумную долю: сколько осталось собрать, поделённое на
    // предполагаемых участников. Это лишь заготовка — её легко стереть.
    const goal = item.priceMinor ?? 0;
    const collected = item.reservation?.collectedMinor ?? 0;
    const left = Math.max(0, goal - collected);
    setAmount(left > 0 ? minorToPriceInput(Math.round(left / 2)) : '');
  }, [item]);

  if (!item) return null;

  const goal = item.priceMinor ?? 0;
  const collected = item.reservation?.collectedMinor ?? 0;
  const left = Math.max(0, goal - collected);

  return (
    <Modal
      open
      title="Скинуться на подарок"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} block>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={pending}
            onClick={() => onConfirm(priceInputToMinor(amount))}
            block
          >
            Участвую
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="strong">{item.title}</div>
        {goal > 0 && (
          <p className="small muted">
            Цель: {formatPrice(goal, item.currency)}. Уже собрано {formatPrice(collected, item.currency)}
            {left > 0 ? `, осталось ${formatPrice(left, item.currency)}` : ' — сумма собрана'}.
          </p>
        )}

        <TextField
          label="Сколько вносите"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          inputMode="decimal"
          placeholder="Можно не указывать"
          hint="Именинник этого не увидит — ни суммы, ни того, что сбор вообще идёт."
        />

        <Notice tone="info" icon={<IconEyeOff size={19} />}>
          <span className="small">Участников видят только другие дарители.</span>
        </Notice>
      </div>
    </Modal>
  );
}

interface SettingsProps {
  open: boolean;
  list: import('@wishare/shared').ListDto;
  onClose: () => void;
  onSaved: () => void;
  onDelete: () => void;
  deleting: boolean;
}

function ListSettingsModal({ open, list, onClose, onSaved, onDelete, deleting }: SettingsProps): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [visibility, setVisibility] = useState(list.visibility);
  const [busy, setBusy] = useState(false);
  /*
   * Название, значок, дата и описание правятся здесь же.
   *
   * Сервер это умел с самого начала (PATCH /lists/:listId принимает title,
   * coverEmoji, eventDate, description), а экрана не было ни одного: список,
   * названный при создании, оставался с этим названием навсегда. Опечатка в
   * заголовке лечилась только удалением списка вместе со всеми желаниями.
   */
  const [title, setTitle] = useState(list.title);
  const [emoji, setEmoji] = useState(list.coverEmoji ?? '🎁');
  const [eventDate, setEventDate] = useState(list.eventDate ?? '');
  const [description, setDescription] = useState(list.description ?? '');
  const [saved, setSaved] = useState(false);

  /*
   * Модалка смонтирована всегда, а не только пока открыта: `useState` возьмёт
   * начальные значения ровно один раз, при первом появлении страницы. Без этой
   * сверки второе открытие показывало бы то, что лежало в полях до сохранения,
   * — то есть прежнее название поверх нового.
   *
   * Зависимость ровно одна — `open`, и это важно дважды. От полей списка
   * эффект зависеть не должен: страница перечитывает список после сохранения,
   * и такая зависимость, во-первых, гасила бы «Сохранено» в тот же миг, когда
   * оно появилось (проверено браузерным сценарием: бейдж не дожидался даже
   * секунды), а во-вторых, затирала бы недописанное человеком, если список
   * обновится, пока он печатает.
   */
  useEffect(() => {
    if (!open) return;
    setTitle(list.title);
    setEmoji(list.coverEmoji ?? '🎁');
    setEventDate(list.eventDate ?? '');
    setDescription(list.description ?? '');
    setSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const естьПравки =
    title.trim() !== list.title ||
    (emoji || null) !== (list.coverEmoji ?? null) ||
    (eventDate || null) !== (list.eventDate ?? null) ||
    (description.trim() || null) !== (list.description ?? null);

  async function saveОписание(): Promise<void> {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.lists.update(list.id, {
        title: title.trim(),
        coverEmoji: emoji || null,
        eventDate: eventDate || null,
        /*
         * Описание уходит строкой, а не null: в схеме оно `optional()` без
         * `nullable()`, и null сервер отверг бы разбором. Пустую строку он сам
         * превращает в null (`input.description || null`) — так стирание
         * описания работает, а проверка на входе не ругается.
         */
        description: description.trim(),
      });
      setSaved(true);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  const rotate = useMutation({
    mutationFn: () => api.lists.rotateShare(list.id),
    onSuccess: onSaved,
  });

  async function save(next: string): Promise<void> {
    setBusy(true);
    try {
      await api.lists.update(list.id, { visibility: next });
      setVisibility(next as typeof visibility);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  async function saveArchived(archived: boolean): Promise<void> {
    setBusy(true);
    try {
      await api.lists.update(list.id, { isArchived: archived });
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Modal open={open} title="Настройки списка" onClose={onClose}>
        <div className="stack">
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div style={{ width: 78 }}>
              <TextField
                label="Значок"
                value={emoji}
                onChange={(event) => setEmoji(event.target.value.slice(0, 4))}
              />
            </div>
            <div className="grow">
              <TextField
                label="Название"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
              />
            </div>
          </div>

          <TextField
            label="Дата события"
            type="date"
            value={eventDate}
            onChange={(event) => setEventDate(event.target.value)}
            hint="Необязательно. Поможет друзьям не опоздать."
          />

          <TextAreaField
            label="Описание"
            placeholder="Пара слов для друзей"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
          />

          <div className="row">
            <Button
              variant="primary"
              size="sm"
              loading={busy}
              disabled={!title.trim() || !естьПравки}
              onClick={() => void saveОписание()}
            >
              Сохранить
            </Button>
            {saved && !естьПравки && <Badge tone="success">Сохранено</Badge>}
          </div>

          <hr className="divider" />

          <div className="field">
            <span className="field__label">Кто увидит список</span>
            <div className="chip-row">
              {(['private', 'friends', 'link', 'public'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`chip ${visibility === value ? 'chip--active' : ''}`.trim()}
                  disabled={busy}
                  onClick={() => void save(value)}
                >
                  {LIST_VISIBILITY_LABEL[value]}
                </button>
              ))}
            </div>
          </div>

          <hr className="divider" />

          <div className="stack stack--sm">
            <span className="field__label">Секретная ссылка</span>
            <p className="tiny muted">
              Если ссылка попала не тем людям, выпустите новую — старая сразу перестанет работать.
            </p>
            <Button variant="ghost" size="sm" loading={rotate.isPending} onClick={() => rotate.mutate()}>
              Выпустить новую ссылку
            </Button>
            {rotate.isSuccess && <Badge tone="success">Готово, старая ссылка больше не откроется</Badge>}
          </div>

          <hr className="divider" />

          {/*
            Архив — мягкая альтернатива удалению. День рождения прошёл, список
            больше не нужен на виду, но выбрасывать его вместе со всеми идеями
            жалко. Заархивированный список уходит с главной и из лент друзей,
            но остаётся доступным владельцу.
          */}
          <div className="stack stack--sm">
            <span className="field__label">Архив</span>
            <p className="tiny muted">
              {list.isArchived
                ? 'Список в архиве: он скрыт с главной и из лент друзей.'
                : 'Уберите список с глаз, не удаляя его. Желания сохранятся.'}
            </p>
            <Button
              variant="ghost"
              size="sm"
              loading={busy}
              onClick={() => void saveArchived(!list.isArchived)}
            >
              {list.isArchived ? 'Вернуть из архива' : 'Убрать в архив'}
            </Button>
          </div>

          <hr className="divider" />

          <Button variant="danger" onClick={() => setConfirmDelete(true)} block>
            Удалить список
          </Button>
        </div>
      </Modal>

      <Confirm
        open={confirmDelete}
        title="Удалить список?"
        message={`«${list.title}» и все желания в нём исчезнут навсегда. Отменить это будет нельзя.`}
        confirmLabel="Удалить навсегда"
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </>
  );
}
